<template>
  <Command
    v-if="!wpsClient.interactive && !wpsClient.media.isMediaOverlay"
    @click="trigger"
  >
    <slot
      v-bind="{
        disabled,
        icon: isPlaying ? 'voice_over_off' : 'record_voice_over',
        label: isPlaying ? t('secondaryMenu.stopTTS') : t('secondaryMenu.tts'),
        toolTip: isPlaying
          ? t('secondaryMenu.stopTTS')
          : t('secondaryMenu.tts'),
      }"
    />
  </Command>
</template>
<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Locator } from "@readium/shared";

import { posthogKeys } from "@/helpers/constants";
import { usePostHog } from "@/helpers/posthog";
import { useTTSStore } from "@/stores/ttsState";
import TTSEngine, { ActiveSentence } from "@/services/tts/TTSEngine";
import { useWpsClient } from "@/wps/client";

import Command from "../command";

const props = defineProps<{
  selection: { text: string; locator?: Locator };
}>();

const { capture: capturePosthogEvent } = usePostHog();

const { t } = useI18n();

const wpsClient = useWpsClient();

const ttsStore = useTTSStore();

const activeSentence = ref<ActiveSentence>(null);

const disabled = !window.speechSynthesis;
const isPlaying = computed<boolean>(
  () => activeSentence.value?.playing || false
);

onUnmounted(() => {
  cancelTTSPlaying();
});

async function trigger() {
  if (disabled) {
    return;
  }

  if (isPlaying.value) {
    return cancelTTSPlaying();
  }

  capturePosthogEvent(posthogKeys.tts);

  activeSentence.value = await TTSEngine.playSentence({
    text: props.selection.text,
    configure: (utterance: SpeechSynthesisUtterance) => {
      ttsStore.configure(utterance);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    highlight: (word: string, start: number, length: number): void => {
      // no-op for now, we just keep the current selection
    },
  });
}

function cancelTTSPlaying() {
  activeSentence.value?.cancel();
  activeSentence.value = null;
}
</script>
